import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowLeft,
    faComments,
    faBug,
    faLightbulb,
    faPalette,
    faStar,
    faTrash,
    faSpinner,
    faExclamationTriangle,
    faShieldAlt,
    faCalendarAlt,
    faUser,
    faFilter,
    faSearch,
    faTimes
} from '@fortawesome/free-solid-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { useAuth } from '../hooks/useAuth';
import { useFeedback } from '../hooks/useFeedback';
import { Feedback } from '../types';
import { isAdmin } from '../utils/adminConfig';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { useTranslation } from 'react-i18next';

const ITEMS_PER_PAGE = 10;

const FEEDBACK_TYPES: { value: string; icon: IconDefinition; color: string }[] = [
    { value: 'general', icon: faComments, color: 'text-blue-500' },
    { value: 'bug', icon: faBug, color: 'text-red-500' },
    { value: 'feature', icon: faLightbulb, color: 'text-yellow-500' },
    { value: 'ui', icon: faPalette, color: 'text-purple-500' }
];

const STATUS_OPTIONS: { value: string; color: string }[] = [
    { value: 'new', color: 'text-red-500' },
    { value: 'read', color: 'text-blue-500' },
    { value: 'in-progress', color: 'text-yellow-500' },
    { value: 'resolved', color: 'text-green-500' }
];

const FeedbackManagementPage: React.FC = () => {
    const { user } = useAuth();
    const { getAllFeedback, deleteFeedback, loading } = useFeedback();
    const { t } = useTranslation();
    const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [filter, setFilter] = useState<string>('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [feedbackToDelete, setFeedbackToDelete] = useState<Feedback | null>(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const loadFeedbacks = useCallback(async () => {
        try {
            const data = await getAllFeedback();
            setFeedbacks(data);
        } catch (error) {
            console.error('Error loading feedbacks:', error);
        }
    }, [getAllFeedback]);

    useEffect(() => {
        if (user && isAdmin(user.email)) {
            loadFeedbacks();
        }
    }, [user, loadFeedbacks]);

    // Derived rather than mirrored in state — the old copy needed an extra
    // effect just to stay in sync with feedbacks/filter/searchTerm.
    const filteredFeedbacks = useMemo(() => {
        let result = feedbacks;

        if (filter !== 'all') {
            result = result.filter(feedback =>
                feedback.type === filter || feedback.status === filter
            );
        }

        const search = searchTerm.trim().toLowerCase();
        if (search) {
            result = result.filter(feedback =>
                feedback.subject.toLowerCase().includes(search) ||
                feedback.message.toLowerCase().includes(search) ||
                feedback.userDisplayName.toLowerCase().includes(search)
            );
        }

        return result;
    }, [feedbacks, filter, searchTerm]);

    const stats = useMemo(() => ({
        total: feedbacks.length,
        new: feedbacks.filter(f => f.status === 'new').length,
        bugs: feedbacks.filter(f => f.type === 'bug').length,
        features: feedbacks.filter(f => f.type === 'feature').length,
    }), [feedbacks]);

    const handleDeleteFeedback = async () => {
        if (!feedbackToDelete) return;

        try {
            setDeleteLoading(true);
            await deleteFeedback(feedbackToDelete.id);
            setFeedbacks(prev => prev.filter(f => f.id !== feedbackToDelete.id));
            setShowDeleteModal(false);
            setFeedbackToDelete(null);
        } catch (error) {
            console.error('Error deleting feedback:', error);
        } finally {
            setDeleteLoading(false);
        }
    };

    const getTypeConfig = (type: string) =>
        FEEDBACK_TYPES.find(item => item.value === type) || FEEDBACK_TYPES[0];

    const getStatusConfig = (status: string) =>
        STATUS_OPTIONS.find(item => item.value === status) || STATUS_OPTIONS[0];

    const formatDate = (timestamp: number) =>
        new Date(timestamp).toLocaleDateString('vi-VN', {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });

    const StarRating = ({ rating }: { rating: number }) => (
        <div className="flex items-center gap-0.5">
            {[1, 2, 3, 4, 5].map((star) => (
                <FontAwesomeIcon
                    key={star}
                    icon={faStar}
                    className={`text-[11px] ${star <= rating ? 'text-yellow-400' : 'text-gray-300 dark:text-gray-600'}`}
                    aria-hidden="true"
                />
            ))}
            <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">({rating})</span>
        </div>
    );

    // Pagination — clamped so deleting the last row of a page cannot strand the user
    const totalPages = Math.max(1, Math.ceil(filteredFeedbacks.length / ITEMS_PER_PAGE));
    const page = Math.min(currentPage, totalPages);
    const startIndex = (page - 1) * ITEMS_PER_PAGE;
    const currentFeedbacks = filteredFeedbacks.slice(startIndex, startIndex + ITEMS_PER_PAGE);

    if (!user) {
        return (
            <div className="glass-page-bg flex items-center justify-center">
                <div className="glass-card p-8 text-center max-w-md">
                    <FontAwesomeIcon icon={faShieldAlt} className="text-4xl text-yellow-500 mb-4" aria-hidden="true" />
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                        {t('admin.feedback.auth.signInRequired')}
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400 mb-4">
                        {t('admin.feedback.auth.signInMessage')}
                    </p>
                    <Link to="/" className="glass-btn-primary inline-flex items-center">
                        <FontAwesomeIcon icon={faArrowLeft} className="mr-2" aria-hidden="true" />
                        {t('admin.feedback.auth.goHome')}
                    </Link>
                </div>
            </div>
        );
    }

    if (!isAdmin(user.email)) {
        return (
            <div className="glass-page-bg flex items-center justify-center">
                <div className="glass-card p-8 text-center max-w-md">
                    <FontAwesomeIcon icon={faShieldAlt} className="text-4xl text-red-500 mb-4" aria-hidden="true" />
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                        {t('admin.feedback.auth.accessDenied')}
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400 mb-4">
                        {t('admin.feedback.auth.adminRequired')}
                    </p>
                    <Link to="/" className="glass-btn-primary inline-flex items-center">
                        <FontAwesomeIcon icon={faArrowLeft} className="mr-2" aria-hidden="true" />
                        {t('admin.feedback.auth.goHome')}
                    </Link>
                </div>
            </div>
        );
    }

    if (loading && feedbacks.length === 0) {
        return <LoadingSpinner />;
    }

    return (
        <div className="glass-page-bg">
            <div className="max-w-6xl mx-auto px-4 py-6">
                {/* Header */}
                <div className="glass-card p-4 mb-6">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                        <div className="flex items-center gap-3">
                            <Link to="/" className="glass-header-back" title={t('admin.feedback.header.backHome')}>
                                <FontAwesomeIcon icon={faArrowLeft} />
                            </Link>
                            <div>
                                <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                                    {t('admin.feedback.header.title')}
                                </h1>
                                <div className="flex items-center gap-2 mt-0.5">
                                    <span className="text-sm font-medium text-indigo-600 dark:text-indigo-400">
                                        {t('admin.feedback.header.adminBadge')}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <button onClick={loadFeedbacks} className="glass-btn-primary !px-3 !py-1.5 text-sm">
                            {t('admin.events.header.refresh')}
                        </button>
                    </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                    <div className="glass-card p-3 text-center">
                        <div className="text-2xl font-bold text-gray-900 dark:text-white">{stats.total}</div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{t('admin.feedback.stats.total')}</p>
                    </div>
                    <div className="glass-card p-3 text-center">
                        <div className="text-2xl font-bold text-red-600 dark:text-red-400">{stats.new}</div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{t('admin.feedback.stats.new')}</p>
                    </div>
                    <div className="glass-card p-3 text-center">
                        <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">{stats.bugs}</div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{t('admin.feedback.stats.bugReports')}</p>
                    </div>
                    <div className="glass-card p-3 text-center">
                        <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{stats.features}</div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{t('admin.feedback.stats.featureRequests')}</p>
                    </div>
                </div>

                {/* Search & Filter */}
                <div className="glass-card p-4 mb-4">
                    <div className="flex flex-col sm:flex-row gap-3">
                        <div className="relative flex-1">
                            <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" aria-hidden="true" />
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                                placeholder={t('admin.feedback.filters.searchPlaceholder')}
                                className="w-full pl-9 pr-8 py-2 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                            {searchTerm && (
                                <button
                                    onClick={() => setSearchTerm('')}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                    aria-label={t('admin.feedback.filters.searchPlaceholder')}
                                >
                                    <FontAwesomeIcon icon={faTimes} className="text-xs" />
                                </button>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            <FontAwesomeIcon icon={faFilter} className="text-gray-400 text-sm" aria-hidden="true" />
                            <select
                                value={filter}
                                onChange={(e) => { setFilter(e.target.value); setCurrentPage(1); }}
                                className="flex-1 sm:flex-none px-3 py-2 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            >
                                <option value="all">{t('admin.feedback.filters.allFeedback')}</option>
                                <optgroup label={t('admin.feedback.filters.typeLabel')}>
                                    {FEEDBACK_TYPES.map(type => (
                                        <option key={type.value} value={type.value}>
                                            {t(`admin.feedback.types.${type.value}`)}
                                        </option>
                                    ))}
                                </optgroup>
                                <optgroup label={t('admin.feedback.filters.statusLabel')}>
                                    {STATUS_OPTIONS.map(status => (
                                        <option key={status.value} value={status.value}>
                                            {t(`admin.feedback.status.${status.value}`)}
                                        </option>
                                    ))}
                                </optgroup>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Feedback List */}
                <div className="space-y-2">
                    {currentFeedbacks.length === 0 ? (
                        <div className="glass-card p-8 text-center">
                            <FontAwesomeIcon icon={faComments} className="text-3xl text-gray-400 mb-3" aria-hidden="true" />
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                                {t('admin.feedback.emptyState.title')}
                            </h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                {searchTerm || filter !== 'all'
                                    ? t('admin.feedback.emptyState.noMatches')
                                    : t('admin.feedback.emptyState.noSubmissions')}
                            </p>
                        </div>
                    ) : (
                        currentFeedbacks.map((feedback) => {
                            const typeConfig = getTypeConfig(feedback.type);
                            const statusConfig = getStatusConfig(feedback.status);

                            return (
                                <div key={feedback.id} className="glass-card p-4">
                                    <div className="flex items-start gap-3">
                                        <div className="w-8 h-8 rounded-lg glass-sub-panel flex items-center justify-center flex-shrink-0">
                                            <FontAwesomeIcon icon={typeConfig.icon} className={`text-xs ${typeConfig.color}`} aria-hidden="true" />
                                        </div>

                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap mb-1.5">
                                                <span className="glass-badge">
                                                    {t(`admin.feedback.types.${typeConfig.value}`)}
                                                </span>
                                                <span className={`text-xs font-medium ${statusConfig.color}`}>
                                                    {t(`admin.feedback.status.${statusConfig.value}`)}
                                                </span>
                                                <StarRating rating={feedback.rating} />
                                            </div>

                                            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1 break-words">
                                                {feedback.subject}
                                            </h3>

                                            <p className="text-sm text-gray-600 dark:text-gray-300 mb-2 whitespace-pre-line break-words">
                                                {feedback.message}
                                            </p>

                                            <div className="flex items-center gap-3 flex-wrap text-xs text-gray-500 dark:text-gray-400">
                                                <span className="flex items-center gap-1">
                                                    <FontAwesomeIcon icon={faCalendarAlt} className="text-[10px]" aria-hidden="true" />
                                                    {formatDate(feedback.timestamp)}
                                                </span>
                                                <span className="flex items-center gap-1 min-w-0">
                                                    <FontAwesomeIcon icon={faUser} className="text-[10px]" aria-hidden="true" />
                                                    <span className="truncate">{feedback.userDisplayName}</span>
                                                </span>
                                            </div>
                                        </div>

                                        <button
                                            onClick={() => {
                                                setFeedbackToDelete(feedback);
                                                setShowDeleteModal(true);
                                            }}
                                            className="w-11 h-11 -m-1.5 flex items-center justify-center flex-shrink-0 rounded-lg text-gray-400 dark:text-gray-500 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                                            title={t('admin.feedback.deleteModal.delete')}
                                            aria-label={t('admin.feedback.deleteModal.delete')}
                                        >
                                            <FontAwesomeIcon icon={faTrash} className="text-sm" />
                                        </button>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>

                {/* Pagination — truncated, matching the other admin pages */}
                {totalPages > 1 && (
                    <div className="glass-card p-3 mt-4 flex items-center justify-between gap-3">
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                            {t('admin.feedback.pagination.showing', {
                                start: startIndex + 1,
                                end: Math.min(startIndex + ITEMS_PER_PAGE, filteredFeedbacks.length),
                                total: filteredFeedbacks.length
                            })}
                        </span>
                        <div className="flex items-center gap-1 flex-shrink-0">
                            <button
                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                disabled={page === 1}
                                aria-label={t('admin.feedback.pagination.previous')}
                                className="px-2.5 py-1 rounded-lg text-xs font-medium disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-gray-700 dark:text-gray-300"
                            >
                                ←
                            </button>
                            {Array.from({ length: totalPages }, (_, i) => i + 1)
                                .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                                .map((pageNum, i, arr) => (
                                    <React.Fragment key={pageNum}>
                                        {i > 0 && arr[i - 1] !== pageNum - 1 && (
                                            <span className="text-xs text-gray-400">…</span>
                                        )}
                                        <button
                                            onClick={() => setCurrentPage(pageNum)}
                                            aria-label={t('admin.feedback.pagination.page', { page: pageNum })}
                                            aria-current={page === pageNum ? 'page' : undefined}
                                            className={`w-7 h-7 rounded-lg text-xs font-medium transition-colors ${
                                                page === pageNum
                                                    ? 'bg-indigo-500 text-white'
                                                    : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                                            }`}
                                        >
                                            {pageNum}
                                        </button>
                                    </React.Fragment>
                                ))}
                            <button
                                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                disabled={page === totalPages}
                                aria-label={t('admin.feedback.pagination.next')}
                                className="px-2.5 py-1 rounded-lg text-xs font-medium disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-gray-700 dark:text-gray-300"
                            >
                                →
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Delete Confirmation Modal */}
            {showDeleteModal && feedbackToDelete && (
                <div className="glass-modal-overlay">
                    <div className="glass-modal p-6 max-w-md w-full">
                        <div className="flex items-center justify-center w-12 h-12 mx-auto rounded-full bg-red-100 dark:bg-red-900/30 mb-4">
                            <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-600 dark:text-red-400 text-xl" aria-hidden="true" />
                        </div>
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white text-center mb-2">
                            {t('admin.feedback.deleteModal.title')}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400 text-center mb-4">
                            {t('admin.feedback.deleteModal.confirmation')}
                        </p>
                        <div className="glass-sub-panel p-3 mb-5">
                            <p className="text-sm font-semibold text-gray-900 dark:text-white break-words">
                                {feedbackToDelete.subject}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                {t('admin.feedback.deleteModal.from')}: {feedbackToDelete.userDisplayName}
                            </p>
                        </div>
                        <div className="flex gap-3">
                            <button
                                onClick={() => {
                                    setShowDeleteModal(false);
                                    setFeedbackToDelete(null);
                                }}
                                disabled={deleteLoading}
                                className="glass-btn-ghost flex-1 !py-2.5"
                            >
                                {t('admin.feedback.deleteModal.cancel')}
                            </button>
                            <button
                                onClick={handleDeleteFeedback}
                                disabled={deleteLoading}
                                className="glass-btn-danger flex-1 !py-2.5 flex items-center justify-center"
                            >
                                {deleteLoading ? (
                                    <FontAwesomeIcon icon={faSpinner} className="animate-spin" aria-hidden="true" />
                                ) : (
                                    t('admin.feedback.deleteModal.delete')
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FeedbackManagementPage;
